// @ts-ignore
import uniloc from "uniloc";

export const ROUTE_CUSTOMERS = "ROUTE_CUSTOMERS";
export const ROUTE_SITES = "ROUTE_SITES";
export const ROUTE_SITE_DASHBOARD = "ROUTE_SITE_DASHBOARD";
export const ROUTE_SITE_AREA_EXPORT = "ROUTE_SITE_AREA_EXPORT";
export const ROUTE_SITE_AREA_EDIT = "ROUTE_SITE_AREA_EDIT";
export const ROUTE_SITE_WORKFLOW_LIST_BY_TYPE =
  "ROUTE_SITE_WORKFLOW_LIST_BY_TYPE";
export const ROUTE_SITE_WORKFLOW = "ROUTE_SITE_WORKFLOW"; // other site-related WFs
export const ROUTE_SITE_WORKFLOW_CREATE = "ROUTE_SITE_WORKFLOW_CREATE";
export const ROUTE_CUSTOMER_WORKFLOW = "ROUTE_CUSTOMER_WORKFLOW";
export const ROUTE_ONBOARDING_CONTINUE = "ROUTE_ONBOARDING_CONTINUE";
export const ROUTE_SITE_FLIGHT_REPORT_RECIPIENTS_EDIT =
  "ROUTE_SITE_FLIGHT_REPORT_RECIPIENTS_EDIT";
export const ROUTE_SITE_MATRIX_CALCULATOR = "ROUTE_SITE_MATRIX_CALCULATOR";
export const ROUTE_SRS_CONFIG = "ROUTE_SRS_CONFIG";

// route must provide customerId and siteId if it present
// implicit routes are not allowed
const routes: { [key: string]: string } = {
  [ROUTE_CUSTOMERS]: "GET /customers",
  [ROUTE_SITES]: "GET /customers/:customerId/sites",
  [ROUTE_SITE_DASHBOARD]: "GET /customers/:customerId/sites/:siteId/dashboard",
  [ROUTE_SITE_AREA_EXPORT]:
    "GET /customers/:customerId/sites/:siteId/export-area",
  [ROUTE_SITE_AREA_EDIT]: "GET /customers/:customerId/sites/:siteId/edit-area",

  // scan will wfs will still technically be accesible partially through
  // direct links since logic is shared
  [ROUTE_SITE_WORKFLOW_LIST_BY_TYPE]:
    "GET /customers/:customerId/sites/:siteId/workflows/:workflowType",

  [ROUTE_SITE_WORKFLOW]:
    "GET /customers/:customerId/sites/:siteId/workflows/:workflowType/:workflowId", // this route is used by Asana Connector
  [ROUTE_SITE_WORKFLOW_CREATE]:
    "GET /customers/:customerId/sites/:siteId/create_workflow/:workflowType", // this route is also used in GCP Request email
  [ROUTE_CUSTOMER_WORKFLOW]: "GET /customers/:customerId/workflow/:workflowId",
  [ROUTE_ONBOARDING_CONTINUE]:
    "GET /customers/:customerId/site/:siteId/continue-onboarding/:workflowId",
  [ROUTE_SITE_FLIGHT_REPORT_RECIPIENTS_EDIT]:
    "GET /customers/:customerId/sites/:siteId/edit-flight-report-recipients",
  [ROUTE_SITE_MATRIX_CALCULATOR]:
    "GET /customers/:customerId/sites/:siteId/matrix-calculator",
  [ROUTE_SRS_CONFIG]: "GET /customers/:customerId/sites/:siteId/srs",
};

const unilocInstance = uniloc(routes, {
  "GET /": ROUTE_CUSTOMERS,
});
// @ts-ignore
const lookup = (window.lookup = unilocInstance.lookup);
const generateRoute = unilocInstance.generate;

const prevRoute: { [key: string]: string } = {
  [ROUTE_SITES]: ROUTE_CUSTOMERS,
  [ROUTE_SITE_DASHBOARD]: ROUTE_SITES,
  [ROUTE_SITE_AREA_EXPORT]: ROUTE_SITE_DASHBOARD,
  [ROUTE_SITE_AREA_EDIT]: ROUTE_SITE_DASHBOARD,
  [ROUTE_SITE_WORKFLOW_LIST_BY_TYPE]: ROUTE_SITE_DASHBOARD,
  [ROUTE_SITE_WORKFLOW]: ROUTE_SITE_WORKFLOW_LIST_BY_TYPE,
  [ROUTE_CUSTOMER_WORKFLOW]: ROUTE_SITES,
  [ROUTE_ONBOARDING_CONTINUE]: ROUTE_SITE_DASHBOARD,
  [ROUTE_SITE_FLIGHT_REPORT_RECIPIENTS_EDIT]: ROUTE_SITE_DASHBOARD,
  [ROUTE_SITE_MATRIX_CALCULATOR]: ROUTE_SITE_DASHBOARD,
  [ROUTE_SRS_CONFIG]: ROUTE_SITE_DASHBOARD,
};

// using route we always can determine customerId even store has not any customers
export function getCurrentCustomerId(route: string): string {
  const { options } = lookup(route);
  return options.customerId || null;
}

export function getCurrentSiteId(route: string): string | null {
  const { options } = lookup(route);
  return options.siteId || null;
}

export function getCurrentWorkflowId(route: string): string {
  const { options } = lookup(route);
  return options.workflowId || "";
}

export function getCurrentWorkflowType(route: string): string {
  const { options } = lookup(route);
  return options.workflowType ? options.workflowType.toUpperCase() : "";
}

export function getPreviousRouteId(route: string): string | null {
  const { name: routeId, options } = lookup(route);
  if (options.previous) {
    return options.previous.toUpperCase();
  }
  return prevRoute[routeId] || null;
}

export function getPreviousRoute(route: string): string | null {
  const previousRouteId = getPreviousRouteId(route);
  if (!previousRouteId) {
    return null;
  }
  const { name: routeId, options: routeOptions } = lookup(route);
  const requiredOptions = getRequiredRouteOptions(previousRouteId);
  // keep only required options
  const options = (requiredOptions || []).reduce(
    (acc, option) => ({
      ...acc,
      [option]: routeOptions[option],
    }),
    {},
  );
  const previousRoute = generateRoute(previousRouteId, options);
  return previousRoute || null;
}

function getRequiredRouteOptions(routeId: string): string[] | null {
  const route = routes[routeId];
  const matches = route.match(/:\w+/gi);
  if (!matches) {
    return null;
  }
  // trim ':'
  return matches.map((m) => m.slice(1));
}

// be sure that workflowId is always lowercased in uri
const generate = (
  routeName: string,
  routeOptions: { [key: string]: string },
) => {
  return routeOptions.workflowType
    ? generateRoute(routeName, {
        ...routeOptions,
        workflowType: routeOptions.workflowType.toLowerCase(),
      })
    : generateRoute(routeName, {
        ...routeOptions,
      });
};

export const generateDashboardRoute = (customerId: string, siteId: string) =>
  generate(ROUTE_SITE_DASHBOARD, { customerId, siteId });

export const generateSitesRoute = (customerId: string) =>
  generate(ROUTE_SITES, { customerId });

export { lookup, generate };
